import { useSnackbarContext } from '@/contexts/SnackbarContext';
import { useState } from 'react';
import { OnboardingChips } from './OnboardingChips';
import { StepContentLayout } from './StepContentLayout';
import { OnboardingFormData, StepContentProps } from './StepContentProps';

export const OnboardingStep4 = ({
  data,
  onChange,
  onNext,
  onBack,
  isLoading = false,
  disabled,
}: StepContentProps) => {
  const { show } = useSnackbarContext();

  const [adPlatforms, setAdPlatforms] = useState<string[]>(
    data.adPlatforms || [],
  );
  const [monthlyAdSpend, setMonthlyAdSpend] = useState<
    OnboardingFormData['monthlyAdSpend'] | ''
  >(data.monthlyAdSpend || '');

  const handleSubmit = () => {
    if (adPlatforms.length === 0) {
      show('Please select at least one ad platform to continue.', 'warn');
      return;
    }
    if (!monthlyAdSpend) {
      show('Please select your monthly ad spend to continue.', 'warn');
      return;
    }

    onNext();
  };

  const platformOptions: {
    value: NonNullable<OnboardingFormData['adPlatforms']>[0];
    label: string;
  }[] = [
    { value: 'meta', label: 'Meta Ads' },
    { value: 'google', label: 'Google Ads' },
    { value: 'tiktok', label: 'TikTok Ads' },
    { value: 'linkedin', label: 'LinkedIn Ads' },
    { value: 'taboola', label: 'Taboola' },
    { value: 'outbrain', label: 'Outbrain' },
    { value: 'bing', label: 'Bing' },
    { value: 'other', label: 'Other' },
  ];

  const spendOptions = [
    { value: '<5k', label: '<$5k' },
    { value: '5k-20k', label: '$5k-$20k' },
    { value: '20k-50k', label: '$20k-$50k' },
    { value: '50k-200k', label: '$50k-$200k' },
    { value: '200k+', label: '$200k+' },
  ];

  return (
    <StepContentLayout
      showBackButton={!!onBack}
      onBack={onBack}
      onNext={handleSubmit}
      isLoading={isLoading}
      nextButtonDisabled={adPlatforms.length === 0 || !monthlyAdSpend}
      disabled={disabled}
    >
      <div className="flex flex-col gap-5">
        {/* Ad Platforms */}
        <OnboardingChips
          label="Ad platforms"
          options={platformOptions.map((o) => o.label)}
          value={adPlatforms.map(
            (p) => platformOptions.find((o) => o.value === p)?.label || '',
          )}
          onChange={(value) => {
            const platforms = (value as string[])
              .map((label) => {
                const option = platformOptions.find((o) => o.label === label);
                return option?.value;
              })
              .filter(
                (v): v is NonNullable<OnboardingFormData['adPlatforms']>[0] =>
                  !!v,
              );

            setAdPlatforms(platforms);
            onChange((prev) => ({
              ...prev,
              adPlatforms: platforms,
            }));
          }}
          multiSelect
          disabled={disabled}
        />

        {/* Monthly Ad Spend */}
        <OnboardingChips
          label="Monthly ad spend"
          options={spendOptions.map((o) => o.label)}
          value={
            spendOptions.find((o) => o.value === monthlyAdSpend)?.label || ''
          }
          onChange={(value) => {
            const option = spendOptions.find((o) => o.label === value);
            setMonthlyAdSpend(option?.value as any);
            onChange((prev) => ({
              ...prev,
              monthlyAdSpend:
                option?.value as OnboardingFormData['monthlyAdSpend'],
            }));
          }}
          disabled={disabled}
        />
      </div>
    </StepContentLayout>
  );
};
